(function GiftTierMerchandising() {
  "use strict";

  if (window.__GiftTierMerchBooted) return;
  window.__GiftTierMerchBooted = true;

  let deliveryMode = null;
  const claiming = new Set();

  const ICON_GIFT = `<svg class="gift-tier-merch__svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 12v8H4v-8M2 7h20v5H2V7zM12 22V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  const ICON_CHEV_L = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const ICON_CHEV_R = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  function getContext() {
    const ctx = window.GiftTierMerchContext || {};
    const metafields = {};
    const raw = ctx.customerMetafields || {};
    for (const [key, value] of Object.entries(raw)) {
      if (value == null) continue;
      metafields[key] = String(value);
    }
    return {
      customerTags: Array.isArray(ctx.customerTags) ? ctx.customerTags : [],
      customerMetafields: metafields,
    };
  }

  function extractNumericId(gid) {
    if (!gid) return null;
    const match = String(gid).match(/\/(\d+)$/);
    return match ? parseInt(match[1], 10) : parseInt(gid, 10);
  }

  async function getCart() {
    const res = await fetch("/cart.js", { cache: "no-store" });
    if (!res.ok) throw new Error("Cart fetch failed");
    return res.json();
  }

  function cartValue(cart) {
    return (cart.items || [])
      .filter((i) => !(i.properties && i.properties._is_gift === "true"))
      .reduce((sum, i) => sum + (i.line_price || 0), 0) / 100;
  }

  async function fetchMerchandising(endpoint, body) {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Merchandising check failed: " + res.status);
    return res.json();
  }

  function showToast(message) {
    const toast = document.createElement("div");
    toast.className = "gift-tier-merch__toast";
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    setTimeout(() => {
      toast.classList.remove("is-visible");
      setTimeout(() => toast.remove(), 250);
    }, 2800);
  }

  async function addClaimedItems(items) {
    const payload = {
      items: items.map((item) => ({
        id: extractNumericId(item.giftVariantId),
        quantity: item.quantity || 1,
        properties: item.properties,
      })),
    };
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || "Failed to add gifts");
    }
    return res.json();
  }

  function isGiftLine(item) {
    const p = item.properties || {};
    return p._is_gift === "true" || p._is_gift === true;
  }

  function giftsInCartForOffer(cart, offerId) {
    return (cart.items || []).filter(
      (item) => isGiftLine(item) && item.properties._gift_offer_id === offerId
    );
  }

  function giftAlreadyInCart(cart, offerId, giftId) {
    return (cart.items || []).some(
      (item) =>
        isGiftLine(item) &&
        item.properties._gift_offer_id === offerId &&
        item.properties._gift_merch_id === giftId
    );
  }

  function canAddGift(offer, cart, giftId) {
    if (giftAlreadyInCart(cart, offer.id, giftId)) return false;
    const claimed = giftsInCartForOffer(cart, offer.id).length;
    if (offer.selectionMode === "PICK_ONE" || offer.selectionMode === "ONE_FIXED") {
      return claimed < 1;
    }
    if (offer.selectionMode === "PICK_MANY") {
      return claimed < (offer.maxSelectable || offer.maxSelections || 1);
    }
    if (offer.selectionMode === "PICK_ALL") {
      return claimed < offer.gifts.length;
    }
    return claimed < 1;
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function flattenGifts(offers) {
    const rows = [];
    for (const offer of offers) {
      for (const gift of offer.gifts || []) {
        rows.push({ offer, gift });
      }
    }
    return rows;
  }

  function setupCarousel(panel) {
    const track = panel.querySelector(".gift-tier-merch__track");
    const prev = panel.querySelector(".gift-tier-merch__nav--prev");
    const next = panel.querySelector(".gift-tier-merch__nav--next");
    if (!track) return;

    const scrollStep = () => Math.max(track.clientWidth * 0.9, 240);

    function updateNav() {
      const maxScroll = track.scrollWidth - track.clientWidth - 2;
      const showNav = track.scrollWidth > track.clientWidth + 8;
      if (prev) {
        prev.disabled = track.scrollLeft <= 2;
        prev.hidden = !showNav;
      }
      if (next) {
        next.disabled = track.scrollLeft >= maxScroll;
        next.hidden = !showNav;
      }
    }

    prev?.addEventListener("click", () => {
      track.scrollBy({ left: -scrollStep(), behavior: "smooth" });
    });
    next?.addEventListener("click", () => {
      track.scrollBy({ left: scrollStep(), behavior: "smooth" });
    });
    track.addEventListener("scroll", updateNav, { passive: true });
    updateNav();
    window.addEventListener("resize", updateNav, { passive: true });
  }

  function renderPanel(root, data, cart) {
    const accent = root.dataset.accentColor || data.settings?.accentColor || "#151515";
    const heading =
      root.dataset.modalTitle ||
      data.settings?.modalTitle ||
      root.dataset.buttonText ||
      data.settings?.buttonText ||
      "Elige tu regalo";
    const subtitle =
      root.dataset.subtitle ||
      "Selecciona un artículo y añádelo a tu pedido sin coste adicional.";

    root.style.setProperty("--gift-merch-accent", accent);

    const rows = flattenGifts(data.offers || []);
    if (!rows.length) {
      root.replaceChildren();
      root.hidden = true;
      root.classList.remove("is-visible");
      return;
    }

    const panel = document.createElement("div");
    panel.className = "gift-tier-merch__panel";
    panel.innerHTML = `
      <div class="gift-tier-merch__header">
        <div class="gift-tier-merch__label">${ICON_GIFT}<span>Regalo incluido</span></div>
        <h3 class="gift-tier-merch__heading">${escapeHtml(heading)}</h3>
        <p class="gift-tier-merch__sub">${escapeHtml(subtitle)}</p>
      </div>
      <div class="gift-tier-merch__carousel">
        <button type="button" class="gift-tier-merch__nav gift-tier-merch__nav--prev" aria-label="Anterior" hidden>${ICON_CHEV_L}</button>
        <div class="gift-tier-merch__track" tabindex="0" role="list"></div>
        <button type="button" class="gift-tier-merch__nav gift-tier-merch__nav--next" aria-label="Siguiente" hidden>${ICON_CHEV_R}</button>
      </div>
    `;

    const track = panel.querySelector(".gift-tier-merch__track");

    rows.forEach(({ offer, gift }) => {
      const inCart = giftAlreadyInCart(cart, offer.id, gift.id);
      const canAdd = canAddGift(offer, cart, gift.id);
      const disabled = inCart || !canAdd;
      const addLabel = inCart ? "Añadido al pedido" : "Añadir al pedido";

      const card = document.createElement("article");
      card.className = "gift-tier-merch__card" + (disabled ? " is-disabled" : "");
      card.setAttribute("role", "listitem");
      card.dataset.offerId = offer.id;
      card.dataset.giftId = gift.id;
      card.innerHTML = `
        <div class="gift-tier-merch__card-media">
          ${
            gift.giftProductImage
              ? `<img src="${escapeHtml(gift.giftProductImage)}" alt="" loading="lazy" />`
              : `<span class="gift-tier-merch__card-placeholder"></span>`
          }
        </div>
        <div class="gift-tier-merch__card-body">
          <h4 class="gift-tier-merch__card-title">${escapeHtml(gift.giftProductTitle)}</h4>
          <p class="gift-tier-merch__card-badge">Gratis</p>
          <button type="button" class="gift-tier-merch__add button" ${disabled ? "disabled" : ""}>
            <span>${escapeHtml(addLabel)}</span>
          </button>
        </div>
      `;

      const btn = card.querySelector(".gift-tier-merch__add");
      if (!disabled) {
        btn.addEventListener("click", () => handleClaim(root, offer, gift, btn));
      }

      track.appendChild(card);
    });

    root.replaceChildren(panel);
    root.hidden = false;
    root.classList.add("is-visible");
    setupCarousel(panel);
  }

  async function handleClaim(root, offer, gift, btn) {
    const key = `${offer.id}:${gift.id}`;
    if (claiming.has(key)) return;
    claiming.add(key);

    const endpoint = root.dataset.endpoint;
    const prevText = btn.querySelector("span")?.textContent || btn.textContent;
    btn.disabled = true;
    if (btn.querySelector("span")) btn.querySelector("span").textContent = "Añadiendo…";
    else btn.textContent = "Añadiendo…";

    try {
      const cart = await getCart();
      const ctx = getContext();
      const result = await fetchMerchandising(endpoint, {
        intent: "claim",
        offerId: offer.id,
        selectedGiftIds: [gift.id],
        cartValue: cartValue(cart),
        customerTags: ctx.customerTags,
        customerMetafields: ctx.customerMetafields,
      });

      if (result.claim?.items?.length) {
        await addClaimedItems(result.claim.items);
      }

      showToast("Regalo añadido al carrito");
      document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

      const freshCart = await getCart();
      const check = await fetchMerchandising(endpoint, {
        intent: "check",
        cartValue: cartValue(freshCart),
        customerTags: ctx.customerTags,
        customerMetafields: ctx.customerMetafields,
      });
      if (check.enabled && check.offers?.length) {
        renderPanel(root, check, freshCart);
      } else {
        root.replaceChildren();
        root.hidden = true;
        root.classList.remove("is-visible");
      }
    } catch (err) {
      btn.disabled = false;
      if (btn.querySelector("span")) btn.querySelector("span").textContent = prevText;
      else btn.textContent = prevText;
      showToast(err.message || "Error al añadir regalo");
    } finally {
      claiming.delete(key);
    }
  }

  async function initRoot(root) {
    const endpoint = root.dataset.endpoint;
    if (!endpoint) return;

    try {
      const cart = await getCart();
      const ctx = getContext();
      const check = await fetchMerchandising(endpoint, {
        intent: "check",
        cartValue: cartValue(cart),
        customerTags: ctx.customerTags,
        customerMetafields: ctx.customerMetafields,
      });

      deliveryMode = check.deliveryMode;
      if (!check.enabled || check.deliveryMode !== "MERCHANDISING" || !check.offers?.length) {
        root.replaceChildren();
        root.hidden = true;
        root.classList.remove("is-visible");
        return;
      }

      renderPanel(root, check, cart);
    } catch (err) {
      console.warn("[GiftTierMerch]", err.message);
      root.hidden = true;
      root.classList.remove("is-visible");
    }
  }

  function scanAndInit() {
    document.querySelectorAll("[id^='gift-tier-merch-']").forEach((root) => {
      initRoot(root);
    });
  }

  function scheduleScan() {
    setTimeout(scanAndInit, 120);
  }

  scanAndInit();

  [
    "cart:refresh",
    "cart:updated",
    "theme:cart:updated",
    "line-item:change:end",
    "cart-drawer:open",
  ].forEach((name) => {
    document.addEventListener(name, scheduleScan);
  });

  window.GiftTierMerchRefresh = scanAndInit;
  window.GiftTierMerchDeliveryMode = function () {
    return deliveryMode;
  };
})();
